import { useTranslation } from 'react-i18next';
import type { AnalyticsFilters } from '@/types/analytics.types';
import type { FundamentalsResponse } from '@/api/types';
import { OuTreeSelect } from '@/components/common/OuTree';
import { useOuScope } from '@/hooks/useOuScope';
import { buildDimensionOptions } from '../utils/dimensionFilter';
import { getCurrentWeekRange } from '../utils/dateRange';

// Mismo default que useAnalyticsFilters.DEFAULT_FILTERS: si difieren, el botón "limpiar filtros"
// aparece al cargar la pantalla aunque el usuario no haya tocado nada.
const { dateFrom: DEFAULT_DATE_FROM, dateTo: DEFAULT_DATE_TO } = getCurrentWeekRange();

interface FilterBarProps {
  filters: AnalyticsFilters;
  setFilter: <K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) => void;
  resetFilters: () => void;
  apiFundamentals?: FundamentalsResponse;
}

export function FilterBar({ filters, setFilter, resetFilters, apiFundamentals }: FilterBarProps) {
  const { t } = useTranslation();
  // El árbol solo ofrece OUs dentro del alcance del usuario: un AdminOU no puede pedir los
  // reportes de una rama que no administra.
  const { scopedOuItems, isUnrestricted } = useOuScope();

  const allOuLabel = isUnrestricted
    ? t('analytics.filterBar.allOrg')
    : t('analytics.filterBar.allMyScope');

  // Un fundamental con Actitudes y Comportamientos configuradas se ofrece por comportamiento y no
  // como una sola opción: es la misma unidad por la que reportan todas las pestañas.
  const dimensionGroups = buildDimensionOptions(apiFundamentals);
  const hasDimensions = dimensionGroups.length > 0;

  const hasFilters =
    filters.ou !== '' ||
    filters.bu !== '' ||
    filters.dept !== '' ||
    filters.gerencia !== '' ||
    filters.fundFilter !== '' ||
    filters.dateFrom !== DEFAULT_DATE_FROM ||
    filters.dateTo !== DEFAULT_DATE_TO;

  return (
    <div className="au-filter-bar">
      {/* Date range */}
      <input
        type="date"
        className="au-date-input"
        value={filters.dateFrom}
        onChange={(e) => setFilter('dateFrom', e.target.value)}
        aria-label={t('analytics.filterBar.dateFrom', 'Desde')}
      />
      <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>–</span>
      <input
        type="date"
        className="au-date-input"
        value={filters.dateTo}
        onChange={(e) => setFilter('dateTo', e.target.value)}
        aria-label={t('analytics.filterBar.dateTo', 'Hasta')}
      />

      <div className="au-filter-bar-sep" />

      {/* Selector de OU: árbol jerárquico, el mismo componente que la pantalla de usuarios.
          Sin selección, el reporte cubre todo el alcance del usuario. */}
      <div className="au-filter-ou">
        <OuTreeSelect
          mode="single"
          variant="dropdown"
          items={scopedOuItems}
          value={filters.ou ? [filters.ou] : []}
          onChange={selectedIds => {
            setFilter('ou', selectedIds[0] ?? '');
            setFilter('bu', '');
            setFilter('dept', '');
          }}
          placeholder={allOuLabel}
        />
      </div>

      <div className="au-filter-bar-sep" />

      {/* Selector de dimensión: comportamientos cuando el fundamental los tiene, el fundamental
          cuando no. Los comportamientos se agrupan bajo su fundamental para no perder el
          contexto: "Aprendiz" suelto no dice de qué fundamental cuelga. */}
      <select
        className="au-cascade-select"
        value={filters.fundFilter}
        onChange={(e) => setFilter('fundFilter', e.target.value)}
        aria-label={t('analytics.filterBar.allDimensions')}
        disabled={!hasDimensions}
      >
        {hasDimensions ? (
          <>
            <option value="">{t('analytics.filterBar.allDimensions')}</option>
            {dimensionGroups.map((group) =>
              group.hasBehaviors ? (
                <optgroup key={group.fundamentalId} label={group.fundamentalName}>
                  {group.options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </optgroup>
              ) : (
                group.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))
              ))}
          </>
        ) : (
          <option value="">{t('common.loading')}</option>
        )}
      </select>

      {hasFilters && (
        <button
          className="au-clear-filters"
          onClick={resetFilters}
          title={t('analytics.filterBar.clearFilters', 'Limpiar filtros')}
          aria-label={t('analytics.filterBar.clearFilters', 'Limpiar filtros')}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
